/**
 * <ReportAssessmentHeader> — the "Préparation du contrôle" colored header of an assessment-linked
 * chapter block on the live report (molecule, L0.1).
 *
 * Mirrors the legacy `AssessmentChaptersWidget` colored header: a subject-tinted banner holding an
 * eyebrow + the `subject · date` line in white, and a light J-n countdown pill whose text carries the
 * urgency color. It reuses the `<Card>` surface (background swapped to the subject token) rather than
 * hand-rolling one, and the `<Badge>` atom for the pill. The preparation `<ProgressBar>` is a sibling
 * section rendered by `<ReportChapterSectionCard>`, not part of this header. No hex.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Card } from "../Card"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { URGENCY_VARIANT } from "../SchoolAssessmentCard/SchoolAssessmentCard"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types"
import { daysUntil, formatDaysUntil, formatShortDate } from "../../../ports/primary/ui/formatters/dateFormatter"
import { ProgressBar } from "../../atoms/ProgressBar"
import { preparationProgress } from "../../../ports/primary/ui/formatters/schoolAssessmentFormatter"

export type ReportAssessmentHeaderProps = {
    assessment: UISchoolAssessment
    testID?: string
}

export function ReportAssessmentHeader(props: ReportAssessmentHeaderProps) {
    const { assessment, testID } = props
    const { subject, date, urgency } = assessment

    const variant = URGENCY_VARIANT[urgency]
    const days = daysUntil(date)
    const daysLabel = formatDaysUntil(days)

    return (
        <Stack gap="3">
            <Card background={subject.color} p="3" testID={testID} style={styles.card}>
                <Row justify="between" align="center">
                    <Stack gap="0" style={styles.shrink}>
                        <Text size="text-sm" uppercase weight="bold" color="text-on-primary" style={styles.eyebrow}>
                            Préparation du contrôle
                        </Text>
                        <Text size="text-base" weight="bold" color="text-on-primary" numberOfLines={1}>
                            {subject.name} · {formatShortDate(date)}
                        </Text>
                    </Stack>
                    <Badge text={daysLabel} variant="neutral" textColor={variant} />
                </Row>
            </Card>
            <Row gap="3" align="center" style={styles.barContainer}>
                <ProgressBar
                    value={preparationProgress(assessment.chaptersProgress)}
                    color={assessment.subject.color}
                    style={styles.bar}
                />
                <Text weight="bold" color={assessment.subject.color}>
                    {preparationProgress(assessment.chaptersProgress)}%
                </Text>
            </Row>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    card: {
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
    },
    shrink: {
        flexShrink: 1,
    },
    eyebrow: {
        opacity: 0.85,
        letterSpacing: 0.5,
    },
    bar: {
        flex: 1,
    },
    barContainer: {
        paddingHorizontal: theme.spacing["4"],
    },
}))
